import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createApiHeaders, getApiBaseUrl } from '../../../../config/api';
import { UI_DELAY_NAVIGATION } from '../../../../config/constants';

/**
 * useTemplateSaving Hook
 * Handles template saving, updating metadata, and updating draft versions
 */
export const useTemplateSaving = (templateId, backTarget, loadTemplate) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleUpdateMetadata = useCallback(
    async formData => {
      try {
        setUpdatingMetadata(true);
        setError(null);
        setSuccess(null);

        if (!formData.name.trim()) {
          setError('Template name is required');
          return;
        }

        const headers = createApiHeaders();

        const metadataPayload = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          category: formData.category,
          tags: formData.tags,
        };

        const response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/${templateId}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(metadataPayload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail ||
              `Failed to update template metadata: ${response.status}`
          );
        }

        setSuccess('Template metadata updated successfully!');
        await loadTemplate(templateId);

        setTimeout(() => {
          navigate(backTarget);
        }, UI_DELAY_NAVIGATION);
      } catch (err) {
        setError(err.message);
      } finally {
        setUpdatingMetadata(false);
      }
    },
    [templateId, navigate, backTarget, loadTemplate]
  );

  const handleUpdateDraft = useCallback(
    async (formData, currentVersionId, currentVersionStatus) => {
      if (!currentVersionId || currentVersionStatus !== 'draft') {
        setError('Can only update draft versions');
        return;
      }

      try {
        setSaving(true);
        setError(null);
        setSuccess(null);

        if (!formData.template.trim()) {
          setError('Template content is required');
          return;
        }

        const headers = createApiHeaders();

        const payload = {
          template_content: formData.template.trim(),
          output_format: formData.output_format,
          llm_config: formData.model_config,
        };

        const response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions/${currentVersionId}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail ||
              `Failed to update draft version: ${response.status}`
          );
        }

        setSuccess('Draft version updated successfully!');
        await loadTemplate(templateId);
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
    },
    [templateId, loadTemplate]
  );

  const handleSave = useCallback(
    async (formData, isEditMode, originalTemplateData, loadTemplate) => {
      try {
        setSaving(true);
        setError(null);
        setSuccess(null);

        if (!formData.name.trim() || !formData.template.trim()) {
          setError('Name and template content are required');
          return;
        }

        const headers = createApiHeaders();
        let response;
        let successMessage;

        if (isEditMode) {
          const metadataFields = ['name', 'description', 'tags', 'category'];
          const versionFields = [
            'template',
            'output_format',
            'status',
            'model_config',
          ];

          const metadataChanged =
            originalTemplateData &&
            metadataFields.some(field => {
              if (field === 'tags') {
                const original = originalTemplateData.tags || [];
                const current = formData.tags || [];
                return (
                  JSON.stringify(original.sort()) !==
                  JSON.stringify(current.sort())
                );
              }
              return originalTemplateData[field] !== formData[field];
            });

          const versionChanged =
            originalTemplateData &&
            versionFields.some(field => {
              if (field === 'template') {
                return (
                  (originalTemplateData.template || '').trim() !==
                  (formData.template || '').trim()
                );
              }
              if (field === 'model_config') {
                return (
                  JSON.stringify(originalTemplateData.model_config) !==
                  JSON.stringify(formData.model_config)
                );
              }
              return originalTemplateData[field] !== formData[field];
            });

          if (metadataChanged && !versionChanged) {
            const metadataPayload = {
              name: formData.name.trim(),
              description: formData.description.trim(),
              category: formData.category,
              tags: formData.tags,
            };

            response = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/${templateId}`,
              {
                method: 'PUT',
                headers,
                body: JSON.stringify(metadataPayload),
              }
            );
            successMessage = 'Template metadata updated successfully!';
          } else if (versionChanged) {
            const versionPayload = {
              name: formData.name.trim(),
              description: formData.description.trim(),
              template_content: formData.template.trim(),
              category: formData.category,
              tags: formData.tags,
              output_format: formData.output_format,
              status: formData.status,
              llm_config: formData.model_config,
            };

            response = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/${templateId}/versions`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify(versionPayload),
              }
            );
            successMessage = 'New template version created successfully!';
          } else {
            setSuccess('No changes to save');
            setSaving(false);
            return;
          }
        } else {
          const payload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            template_content: formData.template.trim(),
            category: formData.category,
            tags: formData.tags,
            output_format: formData.output_format,
            status: formData.status,
            llm_config: formData.model_config,
          };

          response = await fetch(`${getApiBaseUrl()}/api/v1/templates/`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          });
          successMessage = 'Template created successfully!';
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail ||
              `Failed to ${isEditMode ? 'update' : 'create'} template: ${response.status}`
          );
        }

        setSuccess(successMessage);

        if (isEditMode) {
          await loadTemplate(templateId);
        }

        setTimeout(() => {
          navigate(backTarget);
        }, UI_DELAY_NAVIGATION);
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
    },
    [templateId, navigate, backTarget]
  );

  return {
    saving,
    updatingMetadata,
    error,
    success,
    setError,
    setSuccess,
    handleSave,
    handleUpdateMetadata,
    handleUpdateDraft,
  };
};
